import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import CodeIcon from '@mui/icons-material/Code';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ArtifactFrame from './ArtifactFrame';

// The in-chat card for a Catalyst artifact (a freeform HTML/JS mockup, or svg/markdown/code). Shows
// a compact header (title + kind) with a Preview/Code toggle and an expand-to-dialog, and renders
// the sandboxed preview via ArtifactFrame. During streaming it renders live from the tool args; once
// the tool result lands it uses the persisted payload. Mirrors Catalyst's ArtifactPanel but inline.
export default function ArtifactCard({ part }) {
	const payload = part?.state === 'result' ? (part.result || {}) : (part?.args || {});
	const { kind = 'html', content, title, summary, language } = payload;
	const [view, setView] = React.useState('preview');
	const [expanded, setExpanded] = React.useState(false);

	const hasContent = typeof content === 'string' && content.length > 0;
	// `code` and `markdown` have no separate "code vs preview" split worth toggling.
	const togglable = kind === 'html' || kind === 'svg';

	if (!hasContent) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, color: 'text.secondary' }}>
				<CircularProgress size={14} />
				<Typography variant="caption">Building artifact…</Typography>
			</Box>
		);
	}

	const header = (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
			<CodeIcon fontSize="small" color="primary" />
			<Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
				{title || 'Artifact'}
			</Typography>
			<Chip size="small" label={kind} variant="outlined" />
			{togglable && (
				<ToggleButtonGroup
					size="small"
					exclusive
					value={view}
					onChange={(_, v) => v && setView(v)}
					aria-label="Artifact view"
				>
					<ToggleButton value="preview" sx={{ py: 0.1, px: 1, textTransform: 'none' }}>Preview</ToggleButton>
					<ToggleButton value="code" sx={{ py: 0.1, px: 1, textTransform: 'none' }}>Code</ToggleButton>
				</ToggleButtonGroup>
			)}
			<Tooltip title="Expand">
				<IconButton size="small" onClick={() => setExpanded(true)} aria-label="Expand artifact">
					<OpenInFullIcon fontSize="small" />
				</IconButton>
			</Tooltip>
		</Box>
	);

	// In "code" view, show the raw author source (never executed) regardless of kind.
	const body = (h) => (
		view === 'code' && togglable
			? <ArtifactFrame kind="code" content={content} language={language} height={h} />
			: <ArtifactFrame kind={kind} content={content} language={language} height={h} />
	);

	return (
		<>
			<Paper variant="outlined" sx={{ my: 1, p: 1.5, borderColor: 'primary.main' }}>
				{header}
				{summary ? <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{summary}</Typography> : null}
				<Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
					{body(420)}
				</Box>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
					Mockup preview — runs sandboxed with no data access. An admin promotes it into a report component.
				</Typography>
			</Paper>
			<Dialog open={expanded} onClose={() => setExpanded(false)} fullWidth maxWidth="lg">
				<DialogContent sx={{ minHeight: '70vh', p: 1.5 }}>
					{header}
					{body(window.innerHeight ? Math.round(window.innerHeight * 0.68) : 640)}
				</DialogContent>
			</Dialog>
		</>
	);
}
